import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  login,
  logout,
  setError,
  clearError,
  setLoading,
  resetPasswordEmailSent,
  resetPasswordStatus,
} from "../slices/authSlice";
import httpApi from "@src/utils/httpApi";
import { IAuthUser } from "@src/types/user";
import { resetBypass } from "../slices/maintenanceSessionSlice";

const whenLoginSuccess = async ({
  jwt,
  user,
}: {
  jwt: string;
  user: IAuthUser;
}) => {
  let curator = null;
  let role = null;
  let shopCurator = null;
  if (jwt) {
    const { data: roleResponse } = await httpApi.get("/api/users/me", {
      params: {
        populate: "role",
      },
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
    });
    role = roleResponse.role;
    const {
      data: { data: curatorResponse },
    } = await httpApi.get("/api/curators", {
      params: {
        fields: [
          "name",
          "documentId",
          "countryCode",
          "visible",
          "isSubcurator",
          "vinylListEnabled",
          "articleEditor",
          "createdAt",
          "updatedAt",
          "publishedAt",
          "receiveMessages",
          "aiModeration",
        ],
        filters: {
          user: {
            documentId: {
              $eq: user.documentId,
            },
          },
        },
      },
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
    });
    if (curatorResponse.length > 0) {
      curator = curatorResponse[0];
      const {
        data: { data: shopCuratorResponse },
      } = await httpApi.get("/api/shop-curators", {
        params: {
          filters: {
            curator: {
              documentId: {
                $eq: curator.documentId,
              },
            },
          },
          populate: {
            shopRecords: {
              fields: ["*"],
            },
          },
        },
        headers: {
          Authorization: `Bearer ${jwt}`,
        },
      });
      if (shopCuratorResponse.length > 0) {
        shopCurator = shopCuratorResponse[0];
      }
    }
  }
  return {
    curator,
    role,
    shopCurator,
  };
};
export const sendEmailToResetPasswordThunk = createAsyncThunk(
  "auth/sendEmailToResetPassword",
  async ({ email }: { email: string }, { dispatch }) => {
    try {
      dispatch(clearError());
      dispatch(setLoading(true));
      const response = await httpApi.post("/api/auth/forgot-password", {
        email,
      });
      if (response.status === 200) {
        dispatch(resetPasswordEmailSent(true));
      }
      dispatch(setLoading(false));
    } catch {
      dispatch(setLoading(false));
      dispatch(resetPasswordEmailSent(false));
      dispatch(setError("Error sending email"));
    }
  }
);
export const resetPasswordThunk = createAsyncThunk(
  "auth/resetPasswordThunk",
  async (
    { password, code }: { password: string; code: string },
    { dispatch }
  ) => {
    try {
      dispatch(clearError());
      dispatch(setLoading(true));
      const response = await httpApi.post("/api/auth/reset-password", {
        password,
        passwordConfirmation: password,
        code,
      });
      if (response.status === 200) {
        dispatch(resetPasswordStatus("success"));
        const { jwt, user } = response.data;
        const { curator, role, shopCurator } = await whenLoginSuccess({
          jwt,
          user,
        });
        dispatch(login({ token: jwt, user, curator, role, shopCurator }));
      }
      dispatch(setLoading(false));
    } catch {
      dispatch(setLoading(false));
      dispatch(resetPasswordStatus("error"));
      dispatch(setError("Error updating password"));
    }
  }
);

// Login Thunk
export const loginThunk = createAsyncThunk(
  "auth/login",
  async (
    { username, password }: { username: string; password: string },
    { dispatch }
  ) => {
    try {
      dispatch(clearError());
      dispatch(setLoading(true));
      const response = await httpApi.post("/api/auth/local", {
        identifier: username,
        password,
      });
      const { jwt, user } = response.data;
      const { curator, role, shopCurator } = await whenLoginSuccess({
        jwt,
        user,
      });
      dispatch(login({ token: jwt, user, curator, role, shopCurator }));
      dispatch(setLoading(false));
    } catch {
      dispatch(logout());
      dispatch(setLoading(false));
      dispatch(setError("Invalid credentials"));
    }
  }
);

export const logoutThunk = createAsyncThunk(
  "auth/logout",
  async (_, { dispatch }) => {
    dispatch(logout());
    dispatch(resetBypass());
  }
);
